import React from "react";

export type CommercialProformaPreviewDto = {
  incomplete: boolean;
  missing: string[];
  proforma: {
    id: number | null;
    numero: string;
    objet: string | null;
    dateEmission: string;
    dateValidite: string | null;
    devise: string;
    montantHT: number;
    montantTVA: number;
    montantTTC: number;
  };
  client: { nom: string | null; adresse: string | null; telephone: string | null; email: string | null; reference: string | null };
  company: {
    nomLegal: string | null;
    slogan: string | null;
    adresse: string | null;
    telephone: string | null;
    email: string | null;
    siteWeb: string | null;
    rccm: string | null;
    nif: string | null;
    idNat: string | null;
    impot: string | null;
    bank: string | null;
    iban: string | null;
    swift: string | null;
    mobileMoney: string | null;
    conditionsPaiement: string | null;
    signatureName: string | null;
    signatureRole: string | null;
    signatureUrl: string | null;
    mention: string | null;
  };
  lignes: Array<{ description: string | null; quantite: number; prixUnitaireHT: number; montantHT: number; devise: string }>;
  pdf: { status: "PENDING" | "READY"; url: string | null };
};

const NA = "Non renseigné";

function value(v?: string | null): string {
  return v?.trim() ? v.trim() : NA;
}

function date(v?: string | null): string {
  if (!v) return NA;
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium" }).format(new Date(v));
}

function money(n: number, devise: string): string {
  return `${n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${devise}`;
}

export function CommercialProformaPreview({ data }: { data: CommercialProformaPreviewDto }): React.JSX.Element {
  const bankLines = [
    data.company.bank ? `Banque / compte : ${data.company.bank}` : null,
    data.company.iban ? `IBAN : ${data.company.iban}` : null,
    data.company.swift ? `SWIFT : ${data.company.swift}` : null,
    data.company.mobileMoney ? `Mobile money : ${data.company.mobileMoney}` : null,
  ].filter((line): line is string => Boolean(line));

  return (
    <article className="mx-auto min-h-[1123px] w-full max-w-[794px] bg-white px-12 py-10 text-slate-950 shadow-2xl print:shadow-none">
      {data.incomplete ? (
        <div className="mb-5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
          Aperçu incomplet : {data.missing.join(", ")}
        </div>
      ) : null}
      <header className="flex justify-between gap-8 border-b border-slate-200 pb-6">
        <div className="max-w-[58%]">
          <h1 className="text-xl font-black uppercase tracking-wide">{value(data.company.nomLegal)}</h1>
          {data.company.slogan ? <p className="mt-1 text-sm text-slate-600">{data.company.slogan}</p> : null}
          <div className="mt-4 space-y-0.5 text-xs text-slate-600">
            <p className="whitespace-pre-line">{value(data.company.adresse)}</p>
            <p>Tél. {value(data.company.telephone)} · {value(data.company.email)}</p>
            <p>{value(data.company.siteWeb)}</p>
            <p>RCCM : {value(data.company.rccm)} · NIF : {value(data.company.nif)} · ID NAT : {value(data.company.idNat)}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-3xl font-black">PROFORMA</p>
          <p className="mt-2 font-mono text-sm">{value(data.proforma.numero)}</p>
          <p className="mt-4 text-xs text-slate-600">Émission : {date(data.proforma.dateEmission)}</p>
          <p className="text-xs text-slate-600">Validité : {date(data.proforma.dateValidite)}</p>
        </div>
      </header>

      <section className="mt-8 grid grid-cols-2 gap-8 text-sm">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Client</p>
          <p className="mt-2 font-semibold">{value(data.client.nom)}</p>
          <p className="mt-1 whitespace-pre-line text-slate-600">{value(data.client.adresse)}</p>
          <p className="text-slate-600">{value(data.client.telephone)}</p>
          <p className="text-slate-600">{value(data.client.email)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Objet</p>
          <p className="mt-2 font-semibold">{value(data.proforma.objet)}</p>
          <p className="mt-2 text-xs text-slate-500">Cette proforma ne vaut pas facture.</p>
        </div>
      </section>

      <table className="mt-8 w-full border-collapse text-sm">
        <thead>
          <tr className="border-y border-slate-300 bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
            <th className="py-3 pr-3">Prestation</th>
            <th className="py-3 pr-3 text-right">Qté</th>
            <th className="py-3 pr-3 text-right">P.U. HT</th>
            <th className="py-3 text-right">Montant HT</th>
          </tr>
        </thead>
        <tbody>
          {data.lignes.length === 0 ? (
            <tr><td colSpan={4} className="py-8 text-center text-slate-500">Aucune ligne renseignée.</td></tr>
          ) : data.lignes.map((line, i) => (
            <tr key={`${line.description}-${i}`} className="border-b border-slate-100">
              <td className="py-3 pr-3">{value(line.description)}</td>
              <td className="py-3 pr-3 text-right">{line.quantite.toLocaleString("fr-FR")}</td>
              <td className="py-3 pr-3 text-right">{money(line.prixUnitaireHT, line.devise)}</td>
              <td className="py-3 text-right font-medium">{money(line.montantHT, line.devise)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <section className="mt-6 ml-auto w-full max-w-xs space-y-2 text-sm">
        <Line label="Total HT" value={money(data.proforma.montantHT, data.proforma.devise)} />
        <Line label="TVA" value={money(data.proforma.montantTVA, data.proforma.devise)} />
        <Line label="Total TTC" value={money(data.proforma.montantTTC, data.proforma.devise)} strong />
      </section>

      <section className="mt-10 grid grid-cols-2 gap-8 border-t border-slate-200 pt-6 text-xs text-slate-700">
        <div>
          <p className="font-bold text-slate-900">Coordonnées bancaires</p>
          {bankLines.length ? <ul className="mt-2 list-inside list-disc space-y-1">{bankLines.map((line) => <li key={line}>{line}</li>)}</ul> : <p className="mt-2">{NA}</p>}
        </div>
        <div>
          <p className="font-bold text-slate-900">Conditions de paiement</p>
          <p className="mt-2 whitespace-pre-line">{value(data.company.conditionsPaiement)}</p>
        </div>
      </section>

      <footer className="mt-12 flex items-end justify-between gap-8 text-xs text-slate-500">
        <p className="max-w-md whitespace-pre-line">{value(data.company.mention ?? "Cette proforma ne vaut pas facture.")}</p>
        <div className="min-w-[180px] text-center">
          {data.company.signatureUrl ? <img src={data.company.signatureUrl} alt="" className="mx-auto h-16 object-contain" /> : <div className="mx-auto h-16 rounded border border-dashed border-slate-300" />}
          <p className="mt-2 font-semibold text-slate-800">{value(data.company.signatureName)}</p>
          <p>{value(data.company.signatureRole)}</p>
        </div>
      </footer>
    </article>
  );
}

function Line({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return <div className={`flex justify-between border-b border-slate-200 py-1 ${strong ? "font-bold" : ""}`}><span>{label}</span><span>{value}</span></div>;
}
